/**
 * GitHub-style posting calendar (PRD Metrics §2, §12.6: "custom SVG grid … fed by server bucket
 * arrays; the client never buckets, sums, or averages"), restyled per the redesign
 * § Schedule: larger cells, every weekday labelled, and a dark hover tooltip (the server's `date`
 * string + post count) instead of the native `<title>`.
 *
 * The server sends one zero-filled entry per calendar day in the app timezone plus `maxPosts`.
 * Everything below is layout: which column a day sits in, and which of five shades it gets.
 *
 * The performance face swaps the volume ramp for the §8.3 multiplier shade the server already
 * decided (`perf.shade`) — same colour, same meaning as a post's chip. A day with no qualifying
 * value keeps the neutral level-0 grey.
 */

import { useRef, useState } from 'react';
import type { HeatmapDayDTO, ScheduleHeatmapMode } from '@lw/shared';
import { cx } from '../../../components/cx';
import { calendarMonthLabel, calendarWeekday } from '../../../lib/datetime';
import { formatInteger } from '../../../lib/format';

const CELL = 15;
const GAP = 4;
const LEFT_GUTTER = 34;
const TOP_GUTTER = 18;
const LEVELS = 4;

const WEEKDAY_ROWS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** ISO row: Monday = 0 … Sunday = 6. */
function isoRow(isoDate: string): number {
  return (calendarWeekday(isoDate) + 6) % 7;
}

function level(posts: number, maxPosts: number): number {
  if (posts <= 0 || maxPosts <= 0) return 0;
  return Math.min(LEVELS, Math.max(1, Math.ceil((posts / maxPosts) * LEVELS)));
}

/** Cell class per face: the volume ramp, or the server's chip shade (neutral when withheld). */
function cellClass(day: HeatmapDayDTO, maxPosts: number, mode: ScheduleHeatmapMode): string {
  if (mode === 'volume') return `hm-cell hm-cell--${String(level(day.posts, maxPosts))}`;
  return day.perf.shade === null ? 'hm-cell hm-cell--0' : `hm-cell hm-perf--${day.perf.shade}`;
}

export type CalendarHeatmapProps = {
  days: HeatmapDayDTO[];
  maxPosts: number;
  timezone: string;
  mode: ScheduleHeatmapMode;
  /** Opens that day's posts; wired only for days that have any (`day.posts > 0`). */
  onDayClick?: (index: number) => void;
};

export function CalendarHeatmap({
  days,
  maxPosts,
  timezone,
  mode,
  onDayClick,
}: CalendarHeatmapProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<{ x: number; y: number; day: HeatmapDayDTO } | null>(null);

  if (days.length === 0) return null;

  let column = 0;
  const cells = days.map((day, index) => {
    const row = isoRow(day.date);
    if (index > 0 && row === 0) column += 1;
    return { day, index, row, column };
  });

  // An empty square has nothing to list, so it stays inert — the hint appears only where a click
  // leads somewhere.
  const isClickable = (day: HeatmapDayDTO): boolean => onDayClick !== undefined && day.posts > 0;

  const columns = column + 1;
  const width = LEFT_GUTTER + columns * (CELL + GAP);
  const height = TOP_GUTTER + 7 * (CELL + GAP);

  const monthTicks: { label: string; column: number }[] = [];
  let lastMonth = '';
  for (const cell of cells) {
    const month = cell.day.date.slice(0, 7);
    if (month !== lastMonth) {
      lastMonth = month;
      if (monthTicks.every((tick) => tick.column !== cell.column)) {
        monthTicks.push({ label: calendarMonthLabel(cell.day.date), column: cell.column });
      }
    }
  }

  const show = (day: HeatmapDayDTO, row: number, col: number) => {
    const scrollLeft = scrollRef.current?.scrollLeft ?? 0;
    setTip({
      x: LEFT_GUTTER + col * (CELL + GAP) + CELL / 2 - scrollLeft,
      y: TOP_GUTTER + row * (CELL + GAP),
      day,
    });
  };

  return (
    <div className="svg-hover">
      <div className="svg-hover__scroll" ref={scrollRef}>
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${String(width)} ${String(height)}`}
          role="img"
          aria-label={
            mode === 'volume'
              ? `Posts per day, ${timezone}`
              : `Performance per day, ${timezone}`
          }
          onMouseLeave={() => setTip(null)}
        >
          {WEEKDAY_ROWS.map((label, row) => (
            <text
              key={label}
              className="hm-axis"
              x={0}
              y={TOP_GUTTER + row * (CELL + GAP) + CELL - 3}
            >
              {label}
            </text>
          ))}

          {monthTicks.map((tick) => (
            <text
              key={`${tick.label}-${String(tick.column)}`}
              className="hm-axis"
              x={LEFT_GUTTER + tick.column * (CELL + GAP)}
              y={11}
            >
              {tick.label}
            </text>
          ))}

          {cells.map(({ day, index, row, column: col }) => (
            <rect
              key={day.date}
              x={LEFT_GUTTER + col * (CELL + GAP)}
              y={TOP_GUTTER + row * (CELL + GAP)}
              width={CELL}
              height={CELL}
              rx={3}
              className={cx(cellClass(day, maxPosts, mode), isClickable(day) && 'is-clickable')}
              onMouseEnter={() => show(day, row, col)}
              onMouseLeave={() => setTip(null)}
              onClick={isClickable(day) ? () => onDayClick?.(index) : undefined}
            />
          ))}
        </svg>
      </div>

      {tip !== null ? (
        <div className="svg-tip" style={{ left: tip.x, top: tip.y }}>
          {/* The server ships the day already spelled out; the raw ISO date is layout only. */}
          <div className="t-label">{tip.day.label}</div>
          {mode === 'volume' || tip.day.perf.n === 0 ? (
            <div className="t-value">
              {formatInteger(tip.day.posts)} {tip.day.posts === 1 ? 'post' : 'posts'}
            </div>
          ) : (
            <div className="t-value">
              {/* The multiplier string is server-formatted; the count is a raw cell value, separated. */}
              {tip.day.perf.display} median · {formatInteger(tip.day.perf.n)}{' '}
              {tip.day.perf.n === 1 ? 'post' : 'posts'}
            </div>
          )}
          {isClickable(tip.day) ? <div className="t-hint">Click to show posts</div> : null}
        </div>
      ) : null}
    </div>
  );
}

export default CalendarHeatmap;
